import { useState, useEffect } from "react";
import {
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Button
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

export default function Bookmark() {
  const [filter, setFilter] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch API data
  const fetchBookmarks = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await fetch(
        `/api/v1/bookmark/get-bookmark-admin?page=${page}&search=${search}`
      );
      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        let filtered = result.data;

        // ✅ Frontend filtering (if backend doesn't filter)
        if (search.trim()) {
          const lower = search.toLowerCase();
          filtered = result.data.filter((user) => {
            const email = user.user.email.toLowerCase();
            const username = user.user.username.toLowerCase();
            const hasEvent = user.bookmarks.some((bm) =>
              bm.product.event_name.toLowerCase().includes(lower)
            );

            return (
              email.includes(lower) ||
              username.includes(lower) ||
              hasEvent
            );
          });
        }

        setUsers(filtered);
        setCurrentPage(result.currentPage);
        setTotalPages(result.totalPages);
      }
    } catch (error) {
      console.error("Error fetching bookmarks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookmarks(currentPage, filter);
  }, [filter, currentPage]);

  const handleSearch = (e) => setFilter(e.target.value);
  const handleClear = () => setFilter("");

  // Toggle dropdown expand/collapse
  const toggleExpand = (userId) => {
    setExpandedUser((prev) => (prev === userId ? null : userId));
  };

  // Handle pagination change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  return (
    <Box sx={{ padding: 3 }}>
     <Typography
  variant="h4"
  component="h2"
  gutterBottom
  sx={{
    display: 'flex',
    alignItems: 'center',
    color: "#1976d2",
    fontWeight: "bold"   // or a number like 600, 700
  }}
>
   Bookmarks
</Typography>


      {/* Search & Clear */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <TextField
          placeholder="Search by user or event..."
          value={filter}
          onChange={handleSearch}
          size="small"
          sx={{ width: '100%', maxWidth: 400 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
        />
        <Button
          onClick={handleClear}
          variant="outlined"
          color="success"
          startIcon={<ClearIcon />}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Clear
        </Button>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={2}>
            <Table sx={{ minWidth: 650 }} aria-label="bookmarks table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User Email</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Username</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Bookmark Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <>
                      <TableRow 
                        key={user.user.id} 
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          backgroundColor: expandedUser === user.user.id ? 'action.hover' : 'inherit'
                        }}
                      >
                        <TableCell>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              cursor: 'pointer' 
                            }}
                            onClick={() => toggleExpand(user.user.id)}
                          >
                            <IconButton size="small">
                              {expandedUser === user.user.id ? (
                                <ExpandLessIcon color="primary" />
                              ) : (
                                <ExpandMoreIcon color="primary" />
                              )}
                            </IconButton>
                            {user.user.email}
                          </Box>
                        </TableCell>
                        <TableCell>{user.user.username}</TableCell>
                        <TableCell>
                          <Chip 
                            label={user.bookmarks.length} 
                            color="primary" 
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>

                      {/* Expanded bookmarks row */}
                      {expandedUser === user.user.id && (
                        <TableRow>
                          <TableCell colSpan={3} sx={{ py: 2, backgroundColor: 'background.default' }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ ml: 1, fontWeight: 'bold' }}>
                              Bookmarks
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: '#1976d2' }}>
                                    <TableCell sx={{ color:"white" }}>Event Name</TableCell>
                                    <TableCell sx={{ color:"white" }}>Created At</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {user.bookmarks.map((bm, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{bm.product.event_name}</TableCell>
                                      <TableCell>
                                        {new Date(bm.createdAt).toLocaleString()}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No results found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls (MUI) */}
          <Stack spacing={2} direction="row" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Stack>
        </>
      )}
    </Box>
  );
}
